import Line from "./Line.js";
/**
 * An edge is a line segment that connects two vertices. It will either have
 * two or zero vertices, the latter case indicating that the edge is a line
 * at infinity.
 */
export default class Edge {
    #id;
    #polyhedron;
    #vertices;
    #line;
    #color;
    constructor(id, data, polyhedron) {
        this.#id = id;
        this.#polyhedron = polyhedron;
        this.#vertices = data.vertices;
        this.#color = data.color;
        if (!data.line) {
            if (this.#vertices.length !== 2) {
                throw new Error("Invalid edge data");
            }
            this.#line = Line.fromPoints(this.#polyhedron.getVertex(this.#vertices[0]), this.#polyhedron.getVertex(this.#vertices[1]));
        }
        else {
            this.#line = new Line(data.line);
        }
    }
    get id() {
        return this.#id;
    }
    get vertices() {
        return this.#vertices.map((id) => this.#polyhedron.getVertex(id));
    }
    get color() {
        return this.#color;
    }
    set color(value) {
        this.#color = value;
    }
    get nVertices() {
        return this.#vertices.length;
    }
    get length() {
        if (this.#vertices.length !== 2) {
            return Infinity;
        }
        const v0 = this.#polyhedron.getVertex(this.#vertices[0]);
        const v1 = this.#polyhedron.getVertex(this.#vertices[1]);
        return v0.distanceTo(v1);
    }
    getVertexSign(id) {
        if (this.#vertices.length === 0) {
            return 0;
        }
        if (this.#vertices[0] === id) {
            return -1;
        }
        if (this.#vertices[1] === id) {
            return 1;
        }
        return 0;
    }
    dot(line) {
        return this.#line.dot(line);
    }
    isContainedInLine(line) {
        return this.#line.isTheSame(line);
    }
    getIntersection(plane) {
        const v = this.#line.product(plane);
        const n = v.length();
        if (n < 1e-6) {
            return undefined;
        }
        return v.multiplyScalar(1 / n);
    }
    getData() {
        return {
            vertices: this.#vertices,
            line: this.#line.toArray(),
            color: this.#color,
        };
    }
    toString() {
        if (this.#color) {
            return `(${this.#vertices.join(",")}),color=${this.#color}`;
        }
        return `(${this.#vertices.join(",")})`;
    }
    /**
     * This method evaluates the result of intersecting the edge with a half-space
     * defined by a plane equation.
     *
     * If the edge is fully contained in the half-space, the edge is returned as is and
     * no new vertex is created.
     * @param plane
     * @returns A new edge and intersection vertex.
     */
    intersectWithPlane(plane) {
        const P = this.#line.product(plane);
        const n = P.length();
        if (n < 1e-6) {
            // The edge is fully contained in the plane.
            return { type: "EDGE_TANGENT", edge: this };
        }
        P.multiplyScalar(1 / n);
        if (this.#vertices.length === 0) {
            const v = this.#polyhedron.addVertex(P.toArray());
            const vOpp = this.#polyhedron.addOppositeVertex(v.id);
            // The sign is -1 here, because the second vertex is the point
            // of intersection with the plane.
            return {
                type: "EDGE_ADJECENT",
                edge: this.#polyhedron.addEdge({
                    vertices: [vOpp.id, v.id],
                    line: this.#line.toArray(),
                    color: this.#color,
                }),
                vertex: v,
                sign: -1,
            };
        }
        // otherwise there are exactly two vertices
        const [v0, v1] = this.vertices;
        const l0 = Line.fromPoints(v0, P);
        const l1 = Line.fromPoints(v1, P);
        // Case 1: One of the vertices lies on the plane.
        if (l0.norm < 1e-6) {
            if (P.dot(v0) < 0) {
                // So in fact v0 = -P. Since the edge goes from v0 to v1, the
                // it means that it intersects the plane at v0 in the direction
                // opoosite to the normal of the plane, hence the entire edge
                // is inside the half-space.
                return {
                    type: "EDGE_ADJECENT",
                    edge: this,
                    sign: 1,
                    vertex: v0,
                };
            }
            return {
                type: "EDGE_OUTSIDE",
                edge: this,
            };
        }
        if (l1.norm < 1e-6) {
            if (P.dot(v1) > 0) {
                return {
                    type: "EDGE_ADJECENT",
                    edge: this,
                    sign: -1,
                    vertex: v1,
                };
            }
            return {
                type: "EDGE_OUTSIDE",
                edge: this,
            };
        }
        // Case 2: First vertex lies in the half-space.
        if (this.#line.dot(l0) > 0) {
            if (this.#line.dot(l1) > 0) {
                return {
                    type: "EDGE_INSIDE",
                    edge: this,
                };
            }
            const v2 = this.#polyhedron.addVertex(P.toArray());
            return {
                type: "EDGE_ADJECENT",
                vertex: v2,
                sign: -1,
                edge: this.#polyhedron.addEdge({
                    vertices: [v0.id, v2.id],
                    line: this.#line.toArray(),
                    color: this.#color,
                }),
            };
        }
        // Case 3: First vertex lies outside the half-space.
        if (this.#line.dot(l1) > 0) {
            const v2 = this.#polyhedron.addVertex(P.toArray().map((x) => -x));
            return {
                type: "EDGE_ADJECENT",
                vertex: v2,
                sign: 1,
                edge: this.#polyhedron.addEdge({
                    vertices: [v2.id, v1.id],
                    line: this.#line.toArray(),
                    color: this.#color,
                }),
            };
        }
        return {
            type: "EDGE_OUTSIDE",
            edge: this,
        };
    }
}
//# sourceMappingURL=Edge.js.map